import React, { useEffect, useState } from 'react';

import { Box, Button, TextField } from '@mui/material';

import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { userReducer } from '../../store/reducers/user.reducer';
import { getIsImpersonating, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';

const { impersonate } = userReducer.actions;

export const ImpersonatePage = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const isImpersonating = useAppSelector(getIsImpersonating);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user is admin, redirect to forbidden if not
  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  // Reset isSubmitting when impersonation operation completes
  useEffect(() => {
    if (!isImpersonating && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isImpersonating, isSubmitting]);

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError('Please provide an email');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setEmailError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    setIsSubmitting(true);
    dispatch(impersonate({ email }));
  };

  // Don't render anything if user is not admin (will redirect)
  if (!user || !user.roles.includes('ADMIN')) {
    return null;
  }

  return (
    <Box>
      <AdminPageHeader
        title="User Impersonation"
        subtitle="Enter the email address of the user you want to impersonate."
      />
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="User Email"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
          disabled={isSubmitting}
        />
        <Button type="submit" variant="contained" sx={{ mt: 3 }} disabled={isSubmitting}>
          {isSubmitting ? 'Impersonating...' : 'Impersonate User'}
        </Button>
      </Box>
    </Box>
  );
};
